import {
  EXTERNAL_SERVICE_CODE,
  ExternalServiceCodeType,
} from "types/externalService";

export type CleaningSetting = {
  type: string;
  value: string;
};

/**
 * 清掃設定のタイプを定義した定数
 */
export const CLEANING_SETTING_TYPES = {
  fanMode: "fanMode",
  volume: "volume",
  cleanType: "cleanType",
  cleanMode: "cleanMode",
  route: "route",
} as const;

export type CleaningSettingType =
  (typeof CLEANING_SETTING_TYPES)[keyof typeof CLEANING_SETTING_TYPES];

/**
 * 入力値を CleaningSettingType に変換する。不正な値の場合 undefined を返す
 */
export const parseCleaningSettingType = (
  value: string
): CleaningSettingType | undefined => {
  const values = Object.values(CLEANING_SETTING_TYPES);
  return values.includes(value as CleaningSettingType)
    ? (value as CleaningSettingType)
    : undefined;
};

/**
 * Record<string, string> を CleaningSetting に変換する
 */
export const recordToCleaningSetting = (
  data: Record<string, string>
): CleaningSetting[] => {
  return Object.entries(data).map(([type, value]) => ({ type, value }));
};

type OptionItem = {
  value: string;
  label: string;
};

type fieldConfig = {
  options: OptionItem[];
  type: string;
  typeLabel: string;
  defaultValue: string;
  hide?: boolean;
};

const createDreameCleaningSettingFields = (
  cleanType: string
): fieldConfig[] => {
  if (cleanType === "3") {
    return [
      {
        type: "cleanType",
        typeLabel: "清掃タイプ",
        defaultValue: "3",
        options: [
          { value: "1", label: "掃く" },
          { value: "3", label: "拭く" },
          { value: "5", label: "掃く＆拭く" },
        ],
      },
      {
        type: "cleanMode",
        typeLabel: "清掃モード",
        defaultValue: "1",
        options: [
          { value: "0", label: "静音" },
          { value: "1", label: "標準" },
          { value: "2", label: "強力" },
          { value: "3", label: "超強力" },
        ],
        hide: true,
      },
      {
        typeLabel: "ルート",
        type: "route",
        defaultValue: "1",
        options: [
          { value: "1", label: "日常" },
          { value: "2", label: "精密" },
          { value: "4", label: "高速" },
        ],
      },
    ];
  }

  return [
    {
      type: "cleanType",
      typeLabel: "清掃タイプ",
      defaultValue: "1",
      options: [
        { value: "1", label: "掃く" },
        { value: "3", label: "拭く" },
        { value: "5", label: "掃く＆拭く" },
      ],
    },
    {
      type: "cleanMode",
      typeLabel: "清掃モード",
      defaultValue: "1",
      options: [
        { value: "0", label: "静音" },
        { value: "1", label: "標準" },
        { value: "2", label: "強力" },
        { value: "3", label: "超強力" },
      ],
    },
    {
      typeLabel: "ルート",
      type: "route",
      defaultValue: "1",
      options: [
        { value: "1", label: "日常" },
        { value: "4", label: "高速" },
      ],
    },
  ];
};

const volumeOptions: OptionItem[] = Array.from({ length: 10 }, (_, i) => {
  const value = (i + 1).toString();
  return { value, label: value };
});

const createTuyaCleaningSettingFields = (): fieldConfig[] => {
  return [
    {
      type: "fanMode",
      typeLabel: "吸引モード",
      defaultValue: "auto",
      options: [
        { value: "quiet", label: "静音" },
        { value: "auto", label: "標準" },
        { value: "strong", label: "強力" },
      ],
    },
    {
      type: "volume",
      typeLabel: "音量",
      defaultValue: "1",
      options: volumeOptions,
    },
  ];
};

type Opt = {
  cleanType?: string;
};

export const getCleaningSettingFields = (
  code: ExternalServiceCodeType,
  opt?: Opt
): fieldConfig[] => {
  if (code === EXTERNAL_SERVICE_CODE.DREAME) {
    return createDreameCleaningSettingFields(opt?.cleanType ?? "1");
  } else if (code === EXTERNAL_SERVICE_CODE.TUYA) {
    return createTuyaCleaningSettingFields();
  }

  console.warn("CLEANING_SETTING.OPTION: 不正なコード:", code);
  return createTuyaCleaningSettingFields();
};
